import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './packages.css';
import Footer from '../../components/Footer/Footer';
import Notification from '../../components/notification';
import { getSubscriptionStatus, createPayPalOrder } from '../../api/api';

const Packages = () => {
  const navigate = useNavigate();
  const [notification, setNotification] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  
  const packages = [
    {
      name: 'Monthly',
      price: '9.99',
      duration: 'month',
      usage: 'Unlimited',
      features: [
        'Unlimited AI image generation',
        'All art styles included',
        'High resolution outputs',
        'Commercial usage rights',
        'Email support',
        'Download in all formats'
      ]
    },
    {
      name: '6 Months',
      price: '8.99',
      duration: '6months',
      billing: 'Billed as $53.94 every 6 months',
      totalPrice: '53.94',
      usage: 'Unlimited',
      features: [
        'Everything in Monthly plan',
        '10% discount on monthly price',
        'Priority email support',
        'Advanced art customization',
        'Bulk generation feature',
        'Priority queue processing'
      ]
    },
    {
      name: 'Yearly',
      price: '7.99',
      duration: 'year',
      billing: 'Billed as $95.88 per year',
      totalPrice: '95.88',
      usage: 'Unlimited',
      features: [
        'Everything in 6 Months plan',
        '20% discount on monthly price',
        '24/7 Priority support',
        'API access included',
        'Custom style training',
        'Dedicated account manager'
      ]
    }
  ];

  useEffect(() => {
    // Check authentication status from localStorage
    const token = localStorage.getItem('authToken');
    setIsAuthenticated(!!token);

    if (token) {
      fetchSubscriptionStatus();
    }
  }, []);

  const fetchSubscriptionStatus = async () => {
    try {
      const response = await getSubscriptionStatus();
      setSubscription(response.data);
    } catch (error) {
      console.error('Error fetching subscription status:', error);
      setNotification({
        type: 'error',
        message: 'Failed to fetch subscription status'
      });
    }
  };

  const handleSubscribe = async (pkg) => {
    try {
      setIsProcessing(true);
      const token = localStorage.getItem('authToken');
      if (!token) {
        navigate('/sign-in', { state: { from: '/packages' } });
        return;
      }

      const packageDetails = {
        name: pkg.name,
        price: pkg.totalPrice || pkg.price,
        duration: pkg.duration
      };

      const response = await createPayPalOrder(packageDetails);
      const { orderId, links } = response.data;
      
      // Find the approval URL from PayPal links
      const approvalLink = links.find(link => link.rel === "approve");
      if (approvalLink) {
        window.location.href = approvalLink.href;
      } else {
        throw new Error('PayPal approval link not found');
      }
    } catch (error) {
      console.error('Error initiating payment:', error);
      setNotification({
        type: 'error',
        message: 'Failed to initiate payment. Please try again.'
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSignInClick = () => {
    navigate('/sign-in', { state: { from: '/packages' } });
  };

  const renderPackageButton = (pkg) => {
    if (!isAuthenticated) {
      return (
        <button onClick={handleSignInClick} className="package-button">
          Sign in to Subscribe
        </button>
      );
    }

    if (subscription?.status === 'active' && subscription?.package === pkg.name) {
      return (
        <div className="current-plan">
          <span>Current Plan</span>
          <p>Expires: {new Date(subscription.endDate).toLocaleDateString()}</p>
        </div>
      );
    }

    return (
      <button 
        onClick={() => handleSubscribe(pkg)} 
        className="package-button"
        disabled={isProcessing}
      >
        {isProcessing ? 'Processing...' : `Subscribe ${pkg.billing ? '- ' + pkg.billing : '$' + pkg.price + '/' + pkg.duration}`}
      </button>
    );
  };

  return (
    <div className="packages-container">
      <h1>Choose Your Package</h1>
      <p className="packages-subtitle">Unlimited AI Art Generation with Flexible Duration Options</p>
      
      <div className="packages-grid">
        {packages.map((pkg, index) => (
          <div key={index} className={`package-card ${index === 1 ? 'pro' : ''}`}>
            <div className="package-header">
              <h2>{pkg.name}</h2>
              <div className="package-price">
                <span className="amount">${pkg.price}</span>
                <span className="period">/{pkg.duration}</span>
              </div>
              {pkg.billing && <div className="billing-info">{pkg.billing}</div>}
              <div className="usage-info">
                <span className="unlimited">{pkg.usage}</span>
              </div>
            </div>
            <div className="package-features">
              <ul>
                {pkg.features.map((feature, i) => (
                  <li key={i}>{feature}</li>
                ))}
              </ul>
            </div>
            {renderPackageButton(pkg)}
          </div>
        ))}
      </div>

      {notification && (
        <Notification
          type={notification.type}
          message={notification.message}
          onClose={() => setNotification(null)}
        />
      )}
      
      <Footer />
    </div>
  );
};

export default Packages; 